import Vue from 'vue'
import Router from 'vue-router'
import { companyConfig } from './config/companies'
import { commonRoutes } from './routes/common'
import { getCompanyRoutes } from './routes/companyRoutes'
import { getPageRoutes } from './routes/pages'
import pinia from '@/stores'
import { useUserInfoStore } from '@/stores/userInfo'
import { useParametersStore } from '@/stores/parameters'
import { useOrderWorkflowsStore } from '@/stores/orderWorkflows'
import logger from '@/utils/logger'

Vue.use(Router)

// Récupère la configuration de la compagnie active
const company = process.env.VUE_APP_COMPANY
const config = companyConfig[company]

if (!config) {
  throw new Error(`[Router] Configuration introuvable pour la compagnie: "${company}". Vérifiez router/config/companies.js`)
}

/**
 * Construit la route Profile à partir de la config compagnie.
 * Utilise le composant unifié Profile.vue avec le paymentProvider en prop.
 */
function getProfileRoute (companyConf) {
  return {
    name: 'profile',
    path: 'profile',
    meta: {
      translate: 'sidebar.profile',
      roles: ['user'],
      requiresAuth: true,
    },
    component: () => import('@/views/profile/Profile'),
    props: { paymentProvider: companyConf.paymentProvider || 'none' },
  }
}

/**
 * Applique les surcharges de meta.translate si définies dans la config.
 */
function applyTranslateOverrides (routes, overrides = {}) {
  if (!Object.keys(overrides).length) return routes
  return routes.map(route => {
    if (overrides[route.name]) {
      return {
        ...route,
        meta: {
          ...route.meta,
          translate: overrides[route.name],
        },
      }
    }
    return route
  })
}

/**
 * Pour OIDC (uqam) : ajoute beforeEnter sur les routes enfants du layout.
 */
function addOidcBeforeEnter (routes) {
  // Lazy getters pour éviter les dépendances circulaires
  let _ParametersService = null
  const getParametersService = () => {
    if (!_ParametersService) _ParametersService = require('@/services').ParametersService
    return _ParametersService
  }
  let _oidcService = null
  const getOidcService = () => {
    if (!_oidcService) _oidcService = require('@/auth/oidcService').default
    return _oidcService
  }

  const checkBeforeEnter = async (to, from, next) => {
    const oidcSvc = getOidcService()
    if (!oidcSvc.isAuthenticated) {
      next('/login')
      return
    }
    // Load parameters
    try {
      const parametersStore = useParametersStore(pinia)
      if (!parametersStore.parameters.length) {
        const parameters = await getParametersService().listAll()
        parametersStore.setParameters(parameters)
      }
      // Charger les configurations de workflow si pas encore chargées (parité avec Guard.js MSAL)
      const owStore = useOrderWorkflowsStore(pinia)
      if (!owStore.loaded) {
        try {
          await owStore.fetchConfigurations()
        } catch (_err) {
          // Silencieux — les workflows ne sont pas critiques
        }
      }
    } catch (error) {
      logger.error(error)
      next('/error500')
      return
    }
    // If roles of routes is *, client can go in route
    if (to.meta.roles.find(x => x === '*')) {
      next()
      return
    }
    // If User is logged
    if (oidcSvc.isAuthenticated) {
      const found = useUserInfoStore(pinia).info.roles.some(r => to.meta.roles.indexOf(r) >= 0)
      if (!found) {
        if (to.path !== '/unauthorized') {
          next('/unauthorized')
        }
      } else {
        next()
      }
    }
  }

  return routes.map(route => ({
    ...route,
    beforeEnter: (to, from, next) => checkBeforeEnter(to, from, next),
  }))
}

// ─── Construction des routes ────────────────────────────────────────────────────

// 1. Routes communes + Profile + routes spécifiques compagnie
let layoutChildren = [
  ...commonRoutes,
  getProfileRoute(config),
  ...getCompanyRoutes(config.extraRoutes || []),
]

// 2. Appliquer les surcharges de translate si nécessaire
layoutChildren = applyTranslateOverrides(layoutChildren, config.translateOverrides)

// 3. Pour OIDC : ajouter les beforeEnter guards
if (config.auth === 'oidc') {
  layoutChildren = addOidcBeforeEnter(layoutChildren)
}

// 4. Routes de pages (Login, Unauthorized, Error500 + OIDC callbacks si nécessaire)
const pageRoutes = getPageRoutes(config.auth)

// ─── Assemblage final ───────────────────────────────────────────────────────────

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      redirect: '/my_portal',
    },
    {
      path: '/',
      component: () => import('@/views/dashboard/Index'),
      children: layoutChildren,
    },
    {
      path: '/',
      component: () => import('@/views/pages/PagesHome'),
      children: pageRoutes,
    },
    // Not found route
    {
      path: '*',
      redirect: '/',
    },
  ],
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
})

// ─── Guards d'authentification ──────────────────────────────────────────────────
// Note: require() volontaire ici pour briser les dépendances circulaires
// (router ↔ store ↔ apiClient). Sera résolu naturellement en Vue 3 / Pinia.

if (config.auth === 'msal') {
  // MSAL : Guard global via Guard.js
  const { registerGuard } = require('./Guard.js')
  registerGuard(router)
} else if (config.auth === 'oidc') {
  // OIDC : Middleware basé sur oidcService (remplace vuex-oidc)
  const oidcService = require('@/auth/oidcService').default
  router.beforeEach((to, from, next) => {
    if (to.meta && to.meta.isOidcCallback) {
      next()
      return
    }
    if (to.meta && to.meta.isPublic) {
      next()
      return
    }
    if (oidcService.isAuthenticated) {
      next()
    } else {
      // Sauvegarder le chemin pour redirection après login
      oidcService.signIn().catch(() => {
        next('/login')
      })
    }
  })
}

export default router
